import React, { useCallback, useEffect, useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import { FormBuilder, scrollToFirstFormError } from '../../../components/ui';
import {
  getLicenseFormFieldOrder,
  getLicenseFormSections,
} from '../config/licenseFormConfig';
import LicensePresetSelect from './LicensePresetSelect';
import LicenseProfileSearchableSelect from './LicenseProfileSearchableSelect';
import LicenseFormSkeleton from './LicenseFormSkeleton';
import useLicenseSx from '../hooks/useLicenseSx';

export default function LicenseForm({
  form,
  errors,
  updateField,
  license = null,
  features = [],
  metadataLoading = false,
  metadataError = null,
  validationAttempt = 0,
}) {
  const dialogStyles = useLicenseSx().dialog;
  const sections = useMemo(() => getLicenseFormSections(features), [features]);
  const fieldOrder = useMemo(() => getLicenseFormFieldOrder(sections), [sections]);

  useEffect(() => {
    if (validationAttempt > 0 && Object.keys(errors).length > 0) {
      scrollToFirstFormError(errors, fieldOrder);
    }
  }, [validationAttempt, errors, fieldOrder]);

  const renderLicenseField = useCallback(
    (field, ctx) => {
      const { values, errors: fieldErrors, onFieldChange, disabled } = ctx;
      const error = fieldErrors[field.name];

      if (field.name === 'permissionProfileId') {
        return (
          <LicenseProfileSearchableSelect
            label={field.label}
            hint={field.hint}
            required={field.required}
            valueId={values.permissionProfileId}
            selectedProfile={license?.permissionProfile ?? null}
            onChange={(profile) => onFieldChange('permissionProfileId', profile?.id ?? null)}
            error={Boolean(error)}
            helperText={error}
            disabled={disabled || metadataLoading}
            enabled={!metadataLoading}
            noOptionsText={field.noOptionsText}
            placeholder={field.placeholder}
          />
        );
      }

      if (
        field.name === 'durationMonths' ||
        field.name === 'maxUsers' ||
        field.name === 'aiCredits'
      ) {
        return (
          <LicensePresetSelect
            label={field.label}
            hint={field.hint}
            required={field.required}
            value={values[field.name]}
            onChange={(value) => onFieldChange(field.name, value)}
            error={error}
            presetOptions={field.presetOptions}
            placeholder={field.placeholder}
            customInputLabel={field.customInputLabel}
            customHelperText={field.customHelperText}
            formatCustomLabel={field.formatCustomLabel}
            disabled={disabled}
          />
        );
      }

      return null;
    },
    [license, metadataLoading]
  );

  return (
    <Box sx={dialogStyles.formStack}>
      {metadataError ? (
        <Alert severity="warning" sx={dialogStyles.alert}>
          {metadataError}
        </Alert>
      ) : null}

      {metadataLoading ? (
        <LicenseFormSkeleton />
      ) : (
        <FormBuilder
          layout="sections"
          sections={sections}
          values={form}
          errors={errors}
          onFieldChange={updateField}
          disabled={metadataLoading}
          renderField={renderLicenseField}
        />
      )}
    </Box>
  );
}
